import React, { Component } from "react";
import styled from "styled-components";

import { Button } from "../../../Components/Button";
import { Icon } from "../../../Components/Icon";
import { ToolTipBadge } from "../../../Components/ToolTip";
import CanvasPreview from "../../../react/components/CanvasPreview";
import { $ } from "../../../vendor";
import { AiGenerateProgress } from "../GeneratePresentation/Components/AiGenerateProgress";
import SlideAiController from "./SlideAiController";
import { t } from "js/i18n";

const SLIDE_GAP = 20;

const Container = styled.div`
    width: 100%;
    height: 100%;
    background: #444;
    overflow-y: scroll;
    pointer-events: auto;

    &::-webkit-scrollbar-thumb:vertical {
        border-radius: 20px;
        background-color: #666;
        border: 8px 4px 8px 8px solid transparent;
        background-clip: content-box;
    }

    padding: 10px ${SLIDE_GAP}px 30px;
    padding-right: 0px;
`;

const SlidesContainer = styled.div.attrs({ "data-test-id": "slide-ai-ideas-slides" })`
    position: relative;
    grid-area: slides;
    display: flex;
    flex-direction: row;
    gap: ${SLIDE_GAP}px;
    flex-wrap: wrap;
    color: white;
    font-size: 14px;

    /* Dimmed rather than disabled: these are still applicable, they just no longer reflect the slide. */
    opacity: ${props => props.$dimmed ? 0.55 : 1};
    transition: opacity 150ms ease;
`;

const StaleNotice = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 ${SLIDE_GAP}px 12px 0;
    padding: 10px 12px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.08);
    color: #ddd;
    font-size: 12px;
    line-height: 1.35;
`;

const StaleText = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 7px;
    flex: 1;

    .bai-icon {
        flex-shrink: 0;
        font-size: 16px !important;
        color: #bbb !important;
    }
`;

const ProgressContainer = styled.div`
    position: relative;
    overflow: hidden;
    height: 100%;
`;

const SlideCategory = styled.div`
    display: flex;
    gap: 10px;
    flex-direction: column;
`;

const CategoryLabel = styled.div`
    display: flex;
    font-weight: bold;
    padding: 5px 0px 5px;
    align-items: center;
    justify-content: center;
    color: white;
    text-transform: uppercase;
`;

class SlideAiIdeasList extends Component {
    constructor(props) {
        super(props);

        this.state = {
            slideWidth: props.slideWidth ?? 50
        };

        this.hasBeenUnmounted = false;

        this.ref = React.createRef();
    }

    componentDidMount() {
        if (!this.props.slideWidth) {
            this.setState({
                slideWidth: (this.ref.current.getBoundingClientRect().width - SLIDE_GAP * 4 - 30) / 3
            });

            $(window).on("resize", event => {
                this.setState({
                    slideWidth: (this.ref.current.getBoundingClientRect().width - SLIDE_GAP * 4 - 30) / 3
                });
            });
        }
    }

    shouldComponentUpdate(nextProps) {
        return !nextProps.canvasController?.canvas?.isAnimating;
    }

    render() {
        const { slideWidth } = this.state;
        const { onSelect, onRefresh, slideCategories, canvasController, isGenerating, ideasAreStale } = this.props;

        if (isGenerating || !slideCategories || slideCategories.length === 0) {
            return (
                <ProgressContainer ref={this.ref}>
                    <AiGenerateProgress label={t("Generating ideas...")} fontSize={18} />
                </ProgressContainer>
            );
        }

        return (
            <Container ref={this.ref}>
                {ideasAreStale && (
                    <StaleNotice data-test-id="slide-ai-ideas-stale">
                        <StaleText>
                            <Icon>history</Icon>
                            {t("You've edited this slide since these ideas were made, so they still show the older wording.")}
                        </StaleText>
                        {onRefresh && (
                            <Button small ai onClick={onRefresh}>
                                {t("Refresh ideas")}
                            </Button>
                        )}
                    </StaleNotice>
                )}
                <SlidesContainer $dimmed={ideasAreStale}>
                    {slideCategories.map((category, idx) => (
                        <SlideCategory key={idx}>
                            <CategoryLabel>
                                {t("{{category}} slides", { category: category.slideCategory.replace("_", " ").toTitleCase() })}&nbsp;&nbsp;
                                <ToolTipBadge>
                                    <strong>
                                        {t("Why did we suggest {{category}} slides? ({{confidence}}% match)", {
                                            category: category.slideCategory.replace("_", " ").toTitleCase(),
                                            confidence: category.slideConfidence
                                        })}
                                    </strong>
                                    <br />
                                    <br />
                                    {category.slideReasoning}
                                </ToolTipBadge>
                            </CategoryLabel>
                            {
                                category.models.map((model, idx) => (
                                    <CanvasPreview
                                        key={idx}
                                        theme={canvasController?.canvas?.getTheme()}
                                        position={null}
                                        slideModel={model}
                                        scale={slideWidth / 1280}
                                        outlineColor="#555"
                                        onClick={() => onSelect(model)}
                                    />
                                ))
                            }
                        </SlideCategory>
                    ))}
                </SlidesContainer>
            </Container>
        );
    }
}

export default SlideAiController.withState(SlideAiIdeasList);
